import {
  ActionIcon,
  Box,
  Button,
  ComboboxItem,
  FileButton,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  Menu,
  Select,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDebouncedState, useDisclosure, useHotkeys } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconBuildingSkyscraper,
  IconCaretDown,
  IconEdit,
  IconFileImport,
  IconFileTypeCsv,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUser,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { repositoryMdm } from "../../../../_base/_const/_constVar";
import {
  changeSearchValue,
  getValueById,
} from "../../../../_base/helper/FunctionHelper";
import { formatDateTransfer } from "../../../../common/FormatDate/FormatDate";
import { _localization } from "../../../../config/location";
import { MessageResponse } from "../../../../model/MessageResponse";
import {
  getMasterOrgSelect,
  getTblDmEmployeeAll,
} from "../../../../service/getSelectApi";
import CreatePersonnelConfigurationForGoodsManager from "./CreatePersonnelConfigurationForGoodsManager";
import DeletePersonnelConfigurationForGoodsManagement from "./DeletePersonnelConfigurationForGoodsManagement";
import { BASE_API_MDM } from "../../../../config";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";

type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

const PersonnelConfigurationForGoodsManagement = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [visible, { toggle, open, close }] = useDisclosure(false);
  const resetRef = useRef<() => void>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";
  const [search, setSearch] = useState<any>({
    IdChiNhanh: _searchJson && checkedPinSearch ? _searchJson?.IdChiNhanh : "",
    Mnv: _searchJson && checkedPinSearch ? _searchJson?.Mnv : "",
  });
  const [keySearch, setKeySearch] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300,
  );

  const [dataBranchSelect, setDataBranchSelect] = useState<ComboboxItem[]>([]);

  const [dataEmployee, setDataEmployee] = useState<any[]>([]);
  const [dataEmployeeCode, setDataEmployeeCode] = useState<any[]>([]);

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "maChiNhanh",
        header: "Chi nhánh",
        Cell: ({ renderedCellValue, row }) => (
          <Text fw={500} size="12.5px">
            {renderedCellValue} - {row.original.tenChiNhanh}
          </Text>
        ),
      },
      {
        accessorKey: "mnv",
        header: "Nhân sự quản lí hàng hóa",
        size: 200,
        Cell: ({ renderedCellValue, row }) => (
          <Text fw={500} size="12.5px">
            {renderedCellValue} - {row.original.hoVaTen}
          </Text>
        ),
      },
      {
        accessorKey: "soDienThoaiLienLac",
        header: "Số điện thoại",
      },
      {
        accessorKey: "createBy",
        header: "Người tạo",
        Cell: ({ renderedCellValue }) => (
          <Text fw={500} size="12.5px">
            {getValueById(String(renderedCellValue), dataEmployee, "label")}
          </Text>
        ),
      },
      {
        accessorKey: "createDate",
        header: "Ngày tạo",
        Cell: ({ renderedCellValue }: any) => (
          <Text size="12.5px" fw={"500"}>
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
      {
        accessorKey: "lastUpdateBy",
        header: "Người cập nhật cuối",
        Cell: ({ renderedCellValue }) => (
          <Text fw={500} size="12.5px">
            {getValueById(String(renderedCellValue), dataEmployee, "label")}
          </Text>
        ),
      },
      {
        accessorKey: "lastUpdateDate",
        header: "Ngày cập nhật cuối",
        Cell: ({ renderedCellValue }: any) => (
          <Text size="12.5px" fw={"500"}>
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
      {
        accessorKey: "action",
        header: "Thao tác",
        enableSorting: false,
        enableColumnFilter: false,
        size: 70,
        Cell: ({ row }) => (
          <Flex justify={"start"} align={"center"} gap={"xs"}>
            <Tooltip label="Chỉnh sửa">
              <ActionIcon
                variant="light"
                color="orange"
                onClick={() => modalCreateAndEdit(row.original)}
              >
                <IconEdit size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Xóa">
              <ActionIcon
                variant="light"
                color="red"
                onClick={() => modalDelete([Number(row.original.id)])}
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Tooltip>
          </Flex>
        ),
      },
    ],
    [dataEmployee, dataBranchSelect],
  );

  const modalCreateAndEdit = (data?: any) => {
    modals.openConfirmModal({
      title: (
        <Title order={5}>
          {data
            ? "Chỉnh sửa nhân viên quản lý hàng hóa"
            : "Thêm mới nhân viên quản lý hàng hóa"}
        </Title>
      ),
      size: "20vw",
      children: (
        <CreatePersonnelConfigurationForGoodsManager
          onSuccess={fetchData}
          data={data}
        />
      ),
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  const modalDelete = (listId: number[]) => {
    modals.openConfirmModal({
      title: <Title order={5}>Xóa nhân sự quản lý hàng hóa</Title>,
      size: "auto",
      children: (
        <DeletePersonnelConfigurationForGoodsManagement
          onSuccess={fetchData}
          listId={listId}
        />
      ),
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/api/v1/TblItemManager/get-list?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params?.toString()}`;

      const res = await repositoryMdm.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
        table.resetRowSelection();
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({
          ...search,
          keySearch: keySearch,
        }),
      );
    }
  };

  const exportDataExcel = async () => {
    open();
    try {
      let url = `?Skip=${pagination?.pageIndex}&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params.toString()}`;

      const baseUrl = `/api/v1/TblItemManager/export-excel${url}`;

      const _url = BASE_API_MDM + baseUrl;
      const token = localStorage.getItem("token");

      const response = await fetch(_url, {
        method: "GET",
        headers: {
          "Content-Type": "application/vnd.ms-excel",
          Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
        },
      });

      if (!response.ok) {
        NotificationExtension.Fails("Export Excel thất bại !");
      } else {
        const blob = await response.blob();
        const urlss = window.URL.createObjectURL(blob);
        const _name = new Date().toISOString().slice(0, 10);
        let filename = "danh_sach_nhan_su_quan_ly_hang_hoa.xlsx";
        const link = document.createElement("a");
        link.href = urlss;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        NotificationExtension.Success("Export Excel thành công !");
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
    }
  };

  const uploadFile = async (e: File) => {
    if (!e) {
      NotificationExtension.Fails("File rỗng hoặc chưa chọn file !");
      return;
    }
    open();
    // await new Promise((resolve) => setTimeout(resolve, 2000));
    const formData = new FormData();
    formData.append("file", e);

    try {
      const resSaveFile = await repositoryMdm.post<MessageResponse<any>>(
        "/api/v1/TblItemManager/import-excel",
        formData,
      );
      if (resSaveFile && resSaveFile.success) {
        resetRef.current?.();
        NotificationExtension.Success("Import file thành công !");
        setTimeout(() => {
          fetchData();
        }, 1000);
      } else {
        NotificationExtension.Fails("Import file thất bại !");
        resetRef.current?.();
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
      resetRef.current?.();
    }
  };

  const fetchDataEmp = async () => {
    const res = await getTblDmEmployeeAll();

    if (res) {
      const data = res.filter((item) => item.id && item.fullname);
      setDataEmployee(
        data?.map((item) => ({
          label: `${item.code} - ${item.fullname}`,
          value: String(item.id),
        })),
      );
      setDataEmployeeCode(
        data?.map((item) => ({
          label: `${item.code} - ${item.fullname}`,
          value: String(item.code),
        })),
      );
    }
  };

  const getDataBranchSelect = async () => {
    const res = await getMasterOrgSelect();

    if (res) {
      const data = res
        ?.filter((item) => item.text && item.value)
        ?.map((item) => ({
          label: `${item.att1} - ${item.text}`,
          value: String(item.value),
        }));
      setDataBranchSelect(data);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination?.pageIndex, pagination?.pageSize]);

  useEffect(() => {
    Promise.all([fetchDataEmp(), getDataBranchSelect()]);
  }, []);

  const getColumnPinningStorage = () => {
    const columnPinningStorage = localStorage.getItem(
      "columnPinningListVourcher",
    );
    return columnPinningStorage
      ? JSON.parse(columnPinningStorage)
      : {
          left: ["mrt-row-select", "mrt-row-numbers"],
          right: ["enforcement", "action"],
        }; // Giá trị mặc định
  };

  // Lưu columnPinning vào state
  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(
    getColumnPinningStorage(),
  );
  // Hàm lưu vào localStorage
  useEffect(() => {
    localStorage.setItem(
      "columnPinningListVourcher",
      JSON.stringify(columnPinning),
    );
  }, [columnPinning]);

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (250 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: true,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        // left: ["mrt-row-numbers", "maVt"],
        // right: ["action"],
        ...columnPinning,
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onColumnPinningChange: (newState) => {
      setColumnPinning(newState);
    },
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
      columnPinning,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  useHotkeys([
    [
      "Enter",
      () => {
        fetchData();
      },
    ],
  ]);

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                {/* <Title size={15}> Đang upload tài liệu...</Title> */}
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <Grid mt={10} w={"80%"}>
            <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2.5 }}>
              <TextInput
                placeholder="Nhập từ khóa tìm kiếm"
                defaultValue={keySearch}
                onChange={(e) => setKeySearch(e.currentTarget.value)}
                leftSection={<IconSearch color="#15aabf" />}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    fetchData();
                  }
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2.5 }}>
              <Select
                leftSection={<IconBuildingSkyscraper color="#15aabf" />}
                placeholder="Chi nhánh"
                data={dataBranchSelect}
                value={search.IdChiNhanh}
                onChange={(e) => {
                  changeSearchValue(e ? e : "", "IdChiNhanh", setSearch);
                }}
                searchable
                clearable
                nothingFoundMessage="Không tìm thấy dữ liệu !"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2.5 }}>
              <Select
                leftSection={<IconUser color="#15aabf" />}
                placeholder="Nhân viên"
                data={dataEmployeeCode}
                value={search.Mnv}
                onChange={(e) => {
                  changeSearchValue(e ? e : "", "Mnv", setSearch);
                }}
                searchable
                clearable
                nothingFoundMessage="Không tìm thấy dữ liệu !"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
              <Button
                onClick={() => fetchData()}
                leftSection={<IconSearch size={14} />}
              >
                Tìm kiếm
              </Button>
            </Grid.Col>
          </Grid>
          <Flex gap={"md"}>
            <Button
              leftSection={<IconTrash size={16} />}
              color="red"
              disabled={selectIds.length === 0}
              onClick={() => modalDelete(selectIds.map((i: any) => Number(i)))}
            >
              Xóa (Các mục đã chọn)
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              color="green"
              onClick={() => modalCreateAndEdit()}
            >
              Thêm mới
            </Button>
            <Menu>
              <Menu.Target>
                <Button rightSection={<IconCaretDown size={14} />}>
                  Excel
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Flex direction={"column"} gap={2.5}>
                  <Button
                    leftSection={<IconFileTypeCsv size={14} />}
                    onClick={() => exportDataExcel()}
                  >
                    Export Excel
                  </Button>
                  <FileButton
                    resetRef={resetRef}
                    onChange={(e) => {
                      if (e) {
                        if (
                          e &&
                          (e.type ===
                            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                            e.type === "application/vnd.ms-excel")
                        )
                          uploadFile(e);
                        else
                          NotificationExtension.Fails(
                            "File không đúng định đạng Excel !",
                          );
                      }
                    }}
                  >
                    {(props) => (
                      <Button
                        leftSection={<IconFileImport size={14} />}
                        {...props}
                      >
                        Import Excel
                      </Button>
                    )}
                  </FileButton>
                </Flex>
              </Menu.Dropdown>
            </Menu>
          </Flex>
        </Flex>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default PersonnelConfigurationForGoodsManagement;
